import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket, ArrowLeft, Trash2, Check, GripVertical, ChevronDown, Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDeploySettings } from '@/hooks/useDeploySettings';
import { useNetlifyOAuth } from '@/hooks/useNetlifyOAuth';
import { useVercelOAuth } from '@/hooks/useVercelOAuth';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNavigate, Link } from 'react-router-dom';
import type { DeployProvider } from '@/contexts/DeploySettingsContext';
import { ExternalFavicon } from '@/components/ExternalFavicon';
import { normalizeUrl } from '@/lib/faviconUrl';

interface PresetProvider {
  id: string;
  type: 'shakespeare' | 'netlify' | 'vercel' | 'nsite';
  name: string;
  description: string;
  requiresNostr?: boolean;
  apiKeyLabel?: string;
  apiKeyURL?: string;
  proxy?: boolean;
}

const PRESET_PROVIDERS: PresetProvider[] = [
  { id: 'shakespeare', type: 'shakespeare', name: 'Shakespeare Deploy', description: 'Deploy to Shakespeare hosting with Nostr authentication', requiresNostr: true },
  { id: 'nsite', type: 'nsite', name: 'nsite', description: 'Deploy to Nostr as a static website' },
  { id: 'netlify', type: 'netlify', name: 'Netlify', description: 'Deploy to Netlify', apiKeyLabel: 'Personal Access Token', apiKeyURL: 'https://app.netlify.com/user/applications#personal-access-tokens', proxy: true },
  { id: 'vercel', type: 'vercel', name: 'Vercel', description: 'Deploy to Vercel', apiKeyLabel: 'Access Token', apiKeyURL: 'https://vercel.com/account/tokens', proxy: true },
];

function getProviderUrl(provider: DeployProvider | PresetProvider): string | null {
  switch (provider.type) {
    case 'shakespeare':
      return 'host' in provider && provider.host ? normalizeUrl(provider.host) : 'https://shakespeare.diy';
    case 'netlify':
      return 'baseURL' in provider && provider.baseURL ? provider.baseURL : 'https://netlify.com';
    case 'vercel':
      return 'baseURL' in provider && provider.baseURL ? provider.baseURL : 'https://vercel.com';
    default:
      return null;
  }
}

function generateCustomProviderId(type: string): string {
  return `${type}-${crypto.randomUUID().split('-')[0]}`;
}

interface SortableProviderItemProps {
  provider: DeployProvider;
  index: number;
  preset?: PresetProvider;
  onRemove: (index: number) => void;
  onUpdate: (index: number, provider: DeployProvider) => void;
  showDragHandle: boolean;
}

function SortableProviderItem({ provider, index, onRemove, onUpdate, showDragHandle }: SortableProviderItemProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: index });

  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <AccordionItem ref={setNodeRef} style={style} value={String(index)} className="border rounded-lg bg-card">
      <AccordionTrigger className="px-4 py-3 hover:no-underline">
        <div className="flex items-center gap-3">
          {showDragHandle && (
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground hover:text-foreground">
              <GripVertical className="h-4 w-4" />
            </div>
          )}
          {(() => {
            const url = getProviderUrl(provider);
            return url ? <ExternalFavicon url={url} size={16} fallback={<Rocket size={16} className="text-muted-foreground" />} /> : <Rocket size={16} className="text-muted-foreground" />;
          })()}
          <span className="text-sm font-medium">{provider.name}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Name</Label>
            <Input value={provider.name} onChange={(e) => onUpdate(index, { ...provider, name: e.target.value })} className="h-9" />
          </div>
          {provider.type === 'shakespeare' ? (
            <>
              <p className="text-sm text-muted-foreground">{t('shakespeareDeployNostrAuth')}</p>
              <div className="space-y-2">
                <Label className="text-sm">Host (Optional)</Label>
                <Input placeholder="shakespeare.wtf" value={provider.host || ''} onChange={(e) => onUpdate(index, { ...provider, host: e.target.value })} className="h-9" />
              </div>
            </>
          ) : provider.type === 'nsite' ? (
            <>
              <div className="space-y-2">
                <Label className="text-sm">Gateway</Label>
                <Input placeholder="nsite.lol" value={provider.gateway || ''} onChange={(e) => onUpdate(index, { ...provider, gateway: e.target.value })} className="h-9" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Relay URLs (comma-separated)</Label>
                <Input placeholder="wss://relay.nostr.band" value={provider.relayUrls?.join(', ') || ''} onChange={(e) => onUpdate(index, { ...provider, relayUrls: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} className="h-9" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Blossom Servers (comma-separated)</Label>
                <Input placeholder="https://blossom.primal.net/" value={provider.blossomServers?.join(', ') || ''} onChange={(e) => onUpdate(index, { ...provider, blossomServers: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} className="h-9" />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="text-sm">{t('apiKey')}</Label>
                <PasswordInput value={provider.apiKey} onChange={(e) => onUpdate(index, { ...provider, apiKey: e.target.value })} className="h-9" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Base URL (Optional)</Label>
                <Input placeholder={provider.type === 'netlify' ? 'https://api.netlify.com/api/v1' : 'https://api.vercel.com'} value={provider.baseURL || ''} onChange={(e) => onUpdate(index, { ...provider, baseURL: e.target.value })} className="h-9" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={provider.proxy || false} onCheckedChange={(checked) => onUpdate(index, { ...provider, proxy: checked === true })} />
                <Label className="text-sm cursor-pointer">Use CORS Proxy</Label>
              </div>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="text-destructive hover:text-destructive hover:bg-destructive/10">
            <Trash2 className="h-4 w-4 mr-2" />
            {t('delete')}
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export function DeploySettings() {
  const { t } = useTranslation();
  const { settings, removeProvider, setProviders } = useDeploySettings();
  const { user } = useCurrentUser();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const netlifyOAuth = useNetlifyOAuth();
  const vercelOAuth = useVercelOAuth();

  const [presetApiKeys, setPresetApiKeys] = useState<Record<string, string>>({});
  const [forceManualEntry, setForceManualEntry] = useState<Record<string, boolean>>({});
  const [customProviderType, setCustomProviderType] = useState<'shakespeare' | 'netlify' | 'vercel' | 'nsite' | ''>('');
  const [customName, setCustomName] = useState('');
  const [customApiKey, setCustomApiKey] = useState('');
  const [customBaseURL, setCustomBaseURL] = useState('');
  const [customHost, setCustomHost] = useState('');
  const [customProxy, setCustomProxy] = useState(false);
  const [customGateway, setCustomGateway] = useState('');
  const [customRelayUrls, setCustomRelayUrls] = useState('');
  const [customBlossomServers, setCustomBlossomServers] = useState('');

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = settings.providers.findIndex((_, i) => i === active.id);
      const newIndex = settings.providers.findIndex((_, i) => i === over.id);
      setProviders(arrayMove(settings.providers, oldIndex, newIndex));
    }
  };

  const handleAddPresetProvider = (preset: PresetProvider) => {
    if (preset.requiresNostr && !user) return;
    const apiKey = presetApiKeys[preset.id];
    if (!preset.requiresNostr && preset.type !== 'nsite' && !apiKey?.trim()) return;

    let newProvider: DeployProvider;
    if (preset.type === 'shakespeare') {
      newProvider = { id: preset.id, name: preset.name, type: 'shakespeare', ...(preset.proxy && { proxy: true }) };
    } else if (preset.type === 'nsite') {
      newProvider = {
        id: preset.id,
        name: preset.name,
        type: 'nsite',
        gateway: 'nosto.re',
        relayUrls: ['wss://relay.nsite.lol', 'wss://relay.nosto.re', 'wss://relay.nostr.band', 'wss://relay.primal.net', 'wss://relay.damus.io', 'wss://purplerelay.com'],
        blossomServers: ['https://blossom.primal.net', 'https://cdn.sovbit.host'],
      };
    } else if (preset.type === 'netlify') {
      newProvider = { id: preset.id, name: preset.name, type: 'netlify', apiKey: apiKey.trim(), ...(preset.proxy && { proxy: true }) };
    } else {
      newProvider = { id: preset.id, name: preset.name, type: 'vercel', apiKey: apiKey.trim(), ...(preset.proxy && { proxy: true }) };
    }

    setProviders([...settings.providers, newProvider]);
    setPresetApiKeys(prev => ({ ...prev, [preset.id]: '' }));
  };

  const handleAddCustomProvider = () => {
    if (!customProviderType || !customName.trim()) return;

    let newProvider: DeployProvider;
    if (customProviderType === 'shakespeare') {
      newProvider = { id: generateCustomProviderId(customProviderType), name: customName.trim(), type: 'shakespeare', ...(customHost?.trim() && { host: customHost.trim() }), ...(customProxy && { proxy: true }) };
    } else if (customProviderType === 'nsite') {
      newProvider = {
        id: generateCustomProviderId(customProviderType),
        name: customName.trim(),
        type: 'nsite',
        gateway: customGateway.trim() || 'nsite.lol',
        relayUrls: customRelayUrls.trim() ? customRelayUrls.split(',').map(s => s.trim()).filter(Boolean) : ['wss://relay.nostr.band'],
        blossomServers: customBlossomServers.trim() ? customBlossomServers.split(',').map(s => s.trim()).filter(Boolean) : ['https://blossom.primal.net/'],
      };
    } else if (customProviderType === 'netlify') {
      if (!customApiKey.trim()) return;
      newProvider = { id: generateCustomProviderId(customProviderType), name: customName.trim(), type: 'netlify', apiKey: customApiKey.trim(), ...(customBaseURL?.trim() && { baseURL: customBaseURL.trim() }), ...(customProxy && { proxy: true }) };
    } else {
      if (!customApiKey.trim()) return;
      newProvider = { id: generateCustomProviderId(customProviderType), name: customName.trim(), type: 'vercel', apiKey: customApiKey.trim(), ...(customBaseURL?.trim() && { baseURL: customBaseURL.trim() }), ...(customProxy && { proxy: true }) };
    }

    setProviders([...settings.providers, newProvider]);
    setCustomProviderType('');
    setCustomName('');
    setCustomApiKey('');
    setCustomBaseURL('');
    setCustomHost('');
    setCustomProxy(false);
  };

  const configuredProviderIds = settings.providers.map(p => p.id);
  const availablePresets = PRESET_PROVIDERS.filter(preset => !configuredProviderIds.includes(preset.id));

  return (
    <div className="p-6 space-y-8">
      {isMobile && (
        <div className="space-y-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="h-8 px-2 -ml-2 text-muted-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('settings')}
          </Button>
        </div>
      )}

      <div>
        <h1 className="text-xl font-semibold tracking-tight">{t('deploySettings')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('deploySettingsDescriptionLong')}</p>
      </div>

      <div className="space-y-6">
        {/* Configured Providers */}
        {settings.providers.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t('configuredProviders')}</h3>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={settings.providers.map((_, index) => index)} strategy={verticalListSortingStrategy}>
                <Accordion type="multiple" className="space-y-2">
                  {settings.providers.map((provider, index) => (
                    <SortableProviderItem
                      key={index}
                      provider={provider}
                      index={index}
                      preset={PRESET_PROVIDERS.find(p => p.type === provider.type)}
                      onRemove={removeProvider}
                      onUpdate={(i, p) => { const newProviders = [...settings.providers]; newProviders[i] = p; setProviders(newProviders); }}
                      showDragHandle={settings.providers.length > 1}
                    />
                  ))}
                </Accordion>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Available Preset Providers */}
        {availablePresets.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{t('addProvider')}</h3>
            <div className="grid gap-3">
              {availablePresets.map((preset) => {
                const showNostrLoginRequired = preset.requiresNostr && !user;
                const oauthHook = preset.type === 'netlify' ? netlifyOAuth : preset.type === 'vercel' ? vercelOAuth : null;
                const isOAuthConfigured = oauthHook?.isOAuthConfigured ?? false;
                const isOAuthLoading = oauthHook?.isLoading ?? false;
                const oauthError = oauthHook?.error ?? null;

                return (
                  <div key={preset.id} className="border rounded-lg p-4 bg-card space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const url = getProviderUrl(preset);
                          return url ? <ExternalFavicon url={url} size={16} fallback={<Rocket size={16} className="text-muted-foreground" />} /> : <Rocket size={16} className="text-muted-foreground" />;
                        })()}
                        <span className="text-sm font-medium">{preset.name}</span>
                      </div>
                      {preset.apiKeyURL && !isOAuthConfigured && (
                        <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline" onClick={() => window.open(preset.apiKeyURL, '_blank')}>
                          {t('getApiKey')}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{preset.description}</p>

                    {showNostrLoginRequired ? (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">{t('loginToNostrRequired')}</p>
                        <Button asChild size="sm" className="w-full"><Link to="/settings/nostr">{t('goToNostrSettings')}</Link></Button>
                      </div>
                    ) : isOAuthConfigured && !forceManualEntry[preset.id] ? (
                      <div className="space-y-2">
                        <div className="flex gap-0">
                          <Button onClick={() => oauthHook?.initiateOAuth()} disabled={isOAuthLoading} className="flex-1 rounded-r-none gap-2 h-9">
                            {isOAuthLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Connecting...</> : <>Connect to {preset.name}</>}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="default" className="rounded-l-none border-l border-primary-foreground/20 px-2 h-9" disabled={isOAuthLoading}><ChevronDown className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setForceManualEntry(prev => ({ ...prev, [preset.id]: true }))}>Enter API key</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        {oauthError && <p className="text-sm text-destructive">{oauthError}</p>}
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        {!preset.requiresNostr && preset.type !== 'nsite' && (
                          <PasswordInput
                            placeholder={preset.apiKeyLabel || t('enterApiKey')}
                            className="flex-1 h-9"
                            value={presetApiKeys[preset.id] || ''}
                            onChange={(e) => setPresetApiKeys(prev => ({ ...prev, [preset.id]: e.target.value }))}
                            onKeyDown={(e) => e.key === 'Enter' && presetApiKeys[preset.id]?.trim() && handleAddPresetProvider(preset)}
                          />
                        )}
                        <Button
                          onClick={() => handleAddPresetProvider(preset)}
                          disabled={(preset.requiresNostr && !user) || (!preset.requiresNostr && preset.type !== 'nsite' && !presetApiKeys[preset.id]?.trim())}
                          size="sm"
                          className={(preset.requiresNostr || preset.type === 'nsite') ? "w-full h-9" : "h-9 px-4"}
                        >
                          {t('add')}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Custom Provider */}
        <Accordion type="single" collapsible>
          <AccordionItem value="custom-provider" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <Plus className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('addCustomProvider')}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm">{t('providerType')}</Label>
                  <Select value={customProviderType} onValueChange={(value: 'shakespeare' | 'netlify' | 'vercel' | 'nsite') => { setCustomProviderType(value); setCustomApiKey(''); setCustomBaseURL(''); setCustomHost(''); setCustomProxy(false); setCustomGateway(''); setCustomRelayUrls(''); setCustomBlossomServers(''); }}>
                    <SelectTrigger className="h-9"><SelectValue placeholder={t('selectProviderType')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="shakespeare">Shakespeare Deploy</SelectItem>
                      <SelectItem value="nsite">nsite</SelectItem>
                      <SelectItem value="netlify">Netlify</SelectItem>
                      <SelectItem value="vercel">Vercel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {customProviderType && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-sm">{t('providerName')}</Label>
                      <Input placeholder="e.g., My Production Deploy" value={customName} onChange={(e) => setCustomName(e.target.value)} className="h-9" />
                    </div>

                    {customProviderType === 'shakespeare' ? (
                      <>
                        <p className="text-sm text-muted-foreground">{t('shakespeareDeployNostrAuth')}</p>
                        <div className="space-y-2">
                          <Label className="text-sm">Host (Optional)</Label>
                          <Input placeholder="shakespeare.wtf" value={customHost} onChange={(e) => setCustomHost(e.target.value)} className="h-9" />
                        </div>
                      </>
                    ) : customProviderType === 'nsite' ? (
                      <>
                        <div className="space-y-2">
                          <Label className="text-sm">Gateway</Label>
                          <Input placeholder="nsite.lol" value={customGateway} onChange={(e) => setCustomGateway(e.target.value)} className="h-9" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Relay URLs (comma-separated)</Label>
                          <Input placeholder="wss://relay.nostr.band" value={customRelayUrls} onChange={(e) => setCustomRelayUrls(e.target.value)} className="h-9" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Blossom Servers (comma-separated)</Label>
                          <Input placeholder="https://blossom.primal.net/" value={customBlossomServers} onChange={(e) => setCustomBlossomServers(e.target.value)} className="h-9" />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label className="text-sm">{customProviderType === 'netlify' ? 'Personal Access Token' : 'Access Token'}</Label>
                          <PasswordInput placeholder={t('enterApiKey')} value={customApiKey} onChange={(e) => setCustomApiKey(e.target.value)} className="h-9" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Base URL (Optional)</Label>
                          <Input placeholder={customProviderType === 'netlify' ? 'https://api.netlify.com/api/v1' : 'https://api.vercel.com'} value={customBaseURL} onChange={(e) => setCustomBaseURL(e.target.value)} className="h-9" />
                        </div>
                      </>
                    )}

                    <div className="flex items-center gap-2">
                      <Checkbox checked={customProxy} onCheckedChange={(checked) => setCustomProxy(checked === true)} />
                      <Label className="text-sm cursor-pointer">Use CORS Proxy</Label>
                    </div>

                    <Button onClick={handleAddCustomProvider} disabled={!customProviderType || !customName.trim() || (customProviderType !== 'shakespeare' && customProviderType !== 'nsite' && !customApiKey.trim())} size="sm" className="gap-2">
                      <Check className="h-4 w-4" />
                      {t('add')}
                    </Button>
                  </>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}

export default DeploySettings;
